'use client';

import { c as _c } from "react/compiler-runtime";
import { TeamCostPerTaskBars } from '@/domains/pages/ai-spend/components/team-cost-per-task-bars';
import { AVG_SPEND_PER_TASK_CARD_TOOLTIP, AVG_SPEND_PER_TASK_DESCRIPTION, AVG_SPEND_PER_TASK_VISUAL_BLOCK_HEIGHT_PX } from '@/domains/pages/ai-spend/constants';
import type { AiSpendOrgSnapshot, TeamSpendSnapshot } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { sortTeamsBySpendPerTaskDesc } from '@/domains/pages/ai-spend/utils/map-ai-spend-aggregates';
import { computeTopQuartileThreshold } from '@/domains/pages/ai-spend/utils/spend-quartiles';
import { NebulyPageCard } from '@/domains/pages/shared/nebuly-page-card';
import { PAGE_KPI_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { Button } from '@/comps/primitives/button';
import { formatSpend } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
type AvgSpendPerTaskCardProps = {
  org: AiSpendOrgSnapshot;
  teams: TeamSpendSnapshot[];
  onOpenDistribution: () => void;
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  className?: string;
};
export function AvgSpendPerTaskCard(t0) {
  const $ = _c(30);
  const {
    org,
    teams,
    onOpenDistribution,
    hoveredKey,
    onHoverChange,
    className
  } = t0;
  let t1;
  if ($[0] !== teams) {
    t1 = sortTeamsBySpendPerTaskDesc(teams);
    $[0] = teams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sortedTeams = t1;
  let t2;
  if ($[2] !== sortedTeams) {
    t2 = computeTopQuartileThreshold(sortedTeams.map(_temp).filter(_temp2));
    $[2] = sortedTeams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const topQuartileThreshold = t2;
  const t3 = org.spendPerTask == null;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowRight className="size-3" strokeWidth={2} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onOpenDistribution || $[6] !== t3) {
    t5 = <Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground" onClick={onOpenDistribution} disabled={t3}>Distribution{t4}</Button>;
    $[5] = onOpenDistribution;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== className) {
    t6 = cn("min-h-0", className);
    $[8] = className;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== org.spendPerTask) {
    t7 = formatSpend(org.spendPerTask, {
      precision: "exact"
    });
    $[10] = org.spendPerTask;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== org.spendPerTask) {
    t8 = org.spendPerTask != null ? <>{formatSpend(org.spendPerTask)}<span className="text-2xl text-muted-foreground">/task</span></> : "\u2014";
    $[12] = org.spendPerTask;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="flex min-w-0 flex-nowrap items-center gap-3"><span className={PAGE_KPI_NUMBER_CLASS} title={t7}>{t8}</span></div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      height: `${AVG_SPEND_PER_TASK_VISUAL_BLOCK_HEIGHT_PX}px`
    };
    t11 = <p className="text-sm leading-snug text-muted-foreground">{AVG_SPEND_PER_TASK_DESCRIPTION}</p>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  let t12;
  if ($[19] !== hoveredKey || $[20] !== onHoverChange || $[21] !== org.spendPerTask || $[22] !== sortedTeams || $[23] !== topQuartileThreshold) {
    t12 = <div className="flex flex-col overflow-hidden" style={t10}>{t11}<TeamCostPerTaskBars teams={sortedTeams} colorThreshold={topQuartileThreshold} orgAverage={org.spendPerTask} hoveredKey={hoveredKey} onHoverChange={onHoverChange} className="mt-auto shrink-0" /></div>;
    $[19] = hoveredKey;
    $[20] = onHoverChange;
    $[21] = org.spendPerTask;
    $[22] = sortedTeams;
    $[23] = topQuartileThreshold;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t5 || $[27] !== t6 || $[28] !== t9) {
    t13 = <NebulyPageCard title="Avg. AI spend per task" description={AVG_SPEND_PER_TASK_CARD_TOOLTIP} headerTrailing={t5} className={t6} contentClassName="flex flex-col gap-3 !py-3" testId="avg-spend-per-task-card">{t9}{t12}</NebulyPageCard>;
    $[25] = t12;
    $[26] = t5;
    $[27] = t6;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
function _temp2(value) {
  return value != null && Number.isFinite(value);
}
function _temp(team) {
  return team.spendPerTask;
}